import axios, { AxiosError } from 'axios'
import { brandView } from '../brand'

/**
 * The whole admin API layer.
 *
 * Deliberately one file. An operations console has a dozen calls and no
 * refresh flow — spreading that across nine modules the way the current console
 * does buys nothing and hides which endpoints actually exist.
 */

const BASE = import.meta.env.VITE_API_BASE ?? '/admin'
const TOKEN_KEY = 'tothor.admin.token'

export const token = {
  get: () => sessionStorage.getItem(TOKEN_KEY),
  set: (t: string) => sessionStorage.setItem(TOKEN_KEY, t),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
}

const client = axios.create({ baseURL: BASE, timeout: 30_000 })

client.interceptors.request.use((c) => {
  noteActivity()
  const t = token.get()
  if (t) c.headers.set('Authorization', `Bearer ${t}`)
  // The console's brand view — the admin backend filters customer data by it.
  c.headers.set('X-Brand', brandView.value)
  return c
})

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

/* ── keeping an ACTIVE console signed in (2026-10-14) ─────────────────────
 *
 * 🔴 Operators were signed out every 15 minutes, mid-task: the token lives 15
 * minutes and the console had no refresh. Now, while someone is using it, the
 * console renews the token shortly before it expires (`/auth/refresh`). An IDLE
 * console stops renewing and expires on its own — a money console left open on
 * an unattended screen should — and the server caps the whole session at 12 h
 * from sign-in (`ADMIN_SESSION_MAX_HOURS`), whatever the activity.
 *
 * One shared promise, so several triggers at once cause ONE refresh.
 */
const IDLE_MS = 15 * 60_000
const RENEW_BEFORE_MS = 5 * 60_000
let lastActivity = Date.now()
let renewing: Promise<void> | null = null

export function noteActivity() {
  lastActivity = Date.now()
}

/** The token's own `exp`, in ms — read, never trusted for authorisation. */
function expiresAt(t: string): number {
  try {
    const part = (t.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/')
    return Number(JSON.parse(atob(part)).exp) * 1000 || 0
  } catch {
    return 0
  }
}

export function keepAlive(): Promise<void> {
  const t = token.get()
  if (!t || renewing) return renewing ?? Promise.resolve()
  const left = expiresAt(t) - Date.now()
  if (left <= 0 || left > RENEW_BEFORE_MS || Date.now() - lastActivity > IDLE_MS) {
    return Promise.resolve()
  }
  // Straight on axios, not through `call()`: a refused renewal (the 12 h ceiling)
  // must not bounce the operator mid-click — the token is still valid for a few
  // minutes, and the next real request's 401 sends them to sign in.
  renewing = client
    .post<{ access_token: string }>('/auth/refresh')
    .then((r) => token.set(r.data.access_token))
    .catch(() => {})
    .finally(() => {
      renewing = null
    })
  return renewing
}

/** Session storage; renewed while active (above). */
export let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn
}

async function call<T>(fn: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await fn()).data
  } catch (e) {
    const ax = e as AxiosError<{ detail?: string }>
    if (ax.response?.status === 401) {
      token.clear()
      onUnauthorized?.()
    }
    throw new ApiError(
      ax.response?.status ?? 0,
      ax.response?.data?.detail ?? ax.message ?? 'Request failed',
    )
  }
}

const get = <T>(url: string, params?: object) => call<T>(() => client.get<T>(url, { params }))
const post = <T>(url: string, body?: unknown) => call<T>(() => client.post<T>(url, body))
const patch = <T>(url: string, body?: unknown) => call<T>(() => client.patch<T>(url, body))
const del = <T>(url: string) => call<T>(() => client.delete<T>(url))

/* ── types ─────────────────────────────────────────────────────────────── */

/**
 * One referral — who invited whom, and what it paid (2026-09-25).
 *
 * 🔴 The bonus is paid AUTOMATICALLY when the invitee's identity is verified;
 * this console monitors and may revoke. There is no "pay" call, on purpose.
 */
export interface ReferralRow {
  id: string
  code: string
  status: 'PENDING' | 'PAID' | 'REVOKED'
  bonus_quantity: string | null
  grant_id: string | null
  created_at: string
  paid_at: string | null
  revoked_at: string | null
  revoked_by: string | null
  revoked_by_email: string | null
  revoke_reason: string | null
  referrer_id: string
  referrer_email: string
  referrer_first_name: string | null
  referrer_last_name: string | null
  referrer_suspended: boolean
  referee_id: string
  referee_email: string
  referee_first_name: string | null
  referee_last_name: string | null
  referee_kyc_status: string
  referee_email_verified: boolean
  referee_joined_at: string
}

export interface ReferrerRow {
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  suspended: boolean
  invited: number
  paid: number
  pending: number
  revoked: number
  earned: string
}

export interface ReferralsOverview {
  bonus_quantity: string
  bonus_asset: string
  summary: {
    total: number
    pending: number
    paid: number
    revoked: number
    paid_quantity: string
    referrers: number
  }
  referrers: ReferrerRow[]
  referrals: ReferralRow[]
}

/** One customer-to-customer transfer, both parties in full (2026-10-14). */
export interface TransferRow {
  id: string
  /** The sender's brand — transfers are same-brand (2026-10-14). */
  brand: string
  amount: string
  currency: string
  note: string | null
  status: string
  created_at: string
  sender_id: string
  sender_email: string
  sender_first_name: string | null
  sender_last_name: string | null
  recipient_id: string
  recipient_email: string
  recipient_first_name: string | null
  recipient_last_name: string | null
}

export type AdminRole = 'SUPER_ADMIN' | 'ADMIN' | 'SUPPORT' | 'VIEWER'

/** The permissions a server route actually enforces (`ENFORCED_PERMISSIONS`). */
export type Permission =
  | 'users:read' | 'users:write'
  | 'kyc:read' | 'kyc:write'
  | 'cards:write'
  | 'finance:read' | 'withdrawals:write' | 'finance:write'
  | 'admins:write'
  | 'prospects:read' | 'prospects:write'

export interface Admin {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: AdminRole
  last_login_at?: string | null
  created_at?: string
  /**
   * What the role allows, from `/auth/me` (2026-10-14). DISPLAY ONLY — it hides
   * menu entries that would answer 403; every route still checks for itself.
   */
  permissions?: Permission[]
}

/* ── prospects — Tothor's sales pipeline (2026-11-01) ─────────────────────
 * Answered in the Tothor view only; every other view gets a 409.
 */
/** ONE status since 2026-11-03 — it replaced status, interest and converted. */
export type ProspectStatus =
  | 'NEW' | 'NRP_1' | 'NRP_2' | 'NRP_3' | 'INTERESTED_HIGH' | 'INTERESTED_LOW' | 'INTERESTED' | 'CALLBACK' | 'ACCOUNT_OPENED' | 'CONVERTED' | 'NOT_INTERESTED' | 'INVALID'

export interface Prospect {
  id: string
  brand: string
  full_name: string
  email: string | null
  phone: string | null
  birth_year: number | null
  birth_raw: string | null
  source: string
  status: ProspectStatus
  /** YYYY-MM-DD, a calendar day rather than an instant. */
  next_follow_up: string | null
  last_contacted_at: string | null
  created_at: string
  updated_at: string
  /** The Tothor customer with this email, if they opened an account — READ, never stored. */
  account_id: string | null
  notes_count: number
}

export interface ProspectActivity {
  id: string
  /** INTEREST / CONVERTED are history from before 2026-11-03; a STATUS row may carry an old status. */
  kind: 'NOTE' | 'STATUS' | 'INTEREST' | 'CONVERTED' | 'FOLLOW_UP' | 'CREATED'
  body: string | null
  from_value: string | null
  to_value: string | null
  created_at: string
  admin_id: string | null
  admin_email: string | null
  admin_first_name: string | null
  admin_last_name: string | null
}

export interface ProspectDetail extends Prospect {
  activity: ProspectActivity[]
}

export interface ProspectStats {
  totals: {
    total: number
    with_account: number
    account_not_marked: number
    follow_ups_due: number
  }
  by_status: Record<ProspectStatus, number>
  by_age: Record<string, number>
  activity: { date: string; notes: number; changes: number; conversions: number }[]
}

export type ProspectPatch = Partial<{
  status: ProspectStatus
  next_follow_up: string | null
  full_name: string
  phone: string
}>

/** One operator account, as the Team screen lists it. */
export interface Operator {
  id: string
  email: string
  first_name: string
  last_name: string
  role: AdminRole
  is_active: boolean
  last_login_at: string | null
  created_at: string
  updated_at: string
  /** Audit rows they wrote, sign-ins excluded. */
  actions: number
  last_action_at: string | null
}

/** One line of an operator's audit trail. */
export interface OperatorAction {
  id: string
  action: string
  resource_type: string
  resource_id: string | null
  ip_address: string | null
  created_at: string
}

export interface RoleMatrix {
  permissions: Permission[]
  roles: Record<AdminRole, Permission[]>
}

export interface Withdrawal {
  id: string
  user_id: string
  /** Joined server-side — an operator must be able to name who they are paying. */
  user_email: string
  first_name: string | null
  last_name: string | null
  kyc_status: string
  rail: 'sepa' | 'crypto'
  amount: string
  currency: string
  status: string
  iban: string | null
  beneficiary: string | null
  address: string | null
  network: string | null
  reference: string
  failure_reason: string | null
  created_at: string
}

export interface KycRow {
  id: string
  user_id: string
  email?: string
  status: string
  provider: string | null
  level_name?: string | null
  created_at: string
}

/**
 * One thing awaiting a decision, whatever kind it is.
 *
 * The queue deliberately mixes payouts and identity checks: an operator's job is
 * "clear what is waiting", not "visit the payouts page, then visit the identity
 * page". Splitting them by type is a filing decision imposed on the person doing
 * the work.
 */
export type QueueKind = 'payout' | 'identity'

export type Segment = 'needs_verification' | 'no_card' | 'active' | 'dormant'

export interface Balance {
  currency: string
  amount: string
}

/** A directory row — enough to know someone without opening them. */
export interface CustomerRow {
  id: string
  email: string
  /** `tothor` | `1cards` (2026-10-14). NOT `CardDetail.brand`, the card network. */
  brand: string
  email_verified: boolean
  first_name: string | null
  last_name: string | null
  country: string | null
  phone: string | null
  kyc_status: string
  is_suspended: boolean
  created_at: string
  last_activity_at: string | null
  card_count: number
  pending_withdrawals: number
  /**
   * On-ramp prerequisites this customer is missing (`street`, `city`,
   * `post_code`). Non-empty means a CARD deposit will be refused until they
   * fill them in — mirrors `_require_swx_fields` server-side.
   */
  deposit_blocked: string[]
  /**
   * SPENDABLE balance only — `pocket='MAIN'`, exactly what the customer's own
   * dashboard shows. 🔴 Until 2026-09-16 this also carried savings positions, so
   * a granted, locked, never-paid-for 392,465 TTR read as someone's money.
   */
  balances: Balance[]
  /** Savings POSITIONS, per asset, with how much of it was given rather than bought. */
  savings: SavingsPosition[]
  segment: Segment
}

export interface SavingsPosition {
  asset: string
  quantity: string
  /** Quantity that came from a grant (`source='ttr_grant'`), not a purchase. */
  granted: string
}

/* ── vendor data, READ ONLY ────────────────────────────────────────────────
 * What Sumsub, SwipeLux and HyperPay hold about one customer. Fetched per
 * vendor, never in one call: each is a synchronous network hop server-side, and
 * one slow partner must not hold the other two hostage. Every block carries its
 * own `error` for the same reason.
 */
export interface SumsubApplicant {
  applicant_id: string
  created_at: string | null
  level: string | null
  inspection_id: string | null
  review_status: string | null
  review_answer: string | null
  reject_labels: string[]
  reject_reason: string | null
  email: string | null
  phone: string | null
  /** What Sumsub read OFF the documents. */
  extracted: {
    first_name: string | null
    last_name: string | null
    middle_name: string | null
    dob: string | null
    gender: string | null
    country: string | null
    nationality: string | null
    place_of_birth: string | null
    addresses: Record<string, unknown>[]
    id_docs: Record<string, string | null>[]
  }
  /** What WE sent them. A mismatch with `extracted` is a real finding. */
  submitted: Record<string, string | null>
  documents: { step: string; type: string | null; country: string | null; images: number; review: string | null }[]
  error: string | null
}

export interface VendorSumsub {
  configured: boolean
  applicants: SumsubApplicant[]
  error: string | null
}

export interface VendorSwipelux {
  configured: boolean
  customer_id: string | null
  note?: string
  profile?: Record<string, unknown>
  balances?: { currency: string; amount: string; chain?: string; address?: string }[]
  wallets?: unknown[]
  rails?: unknown[]
  capabilities?: unknown[]
  accounts?: unknown[]
  error: string | null
}

export interface VendorHypercard {
  configured: boolean
  cards: {
    local: Record<string, unknown>
    balance?: unknown
    transactions?: Record<string, unknown>[]
    application?: unknown
    error: string | null
  }[]
  error: string | null
}

export interface CardDetail {
  id: string
  status: string
  last_4: string | null
  brand: string | null
  card_currency: string | null
  cardholder_name: string | null
  /** HyperPay's raw status integer, as text: '10' frozen via API, '18' frozen by the issuer. */
  hypercard_status: string | null
  created_at: string
}

export interface TxnRow {
  id: string
  direction: 'DEBIT' | 'CREDIT'
  amount: string
  currency: string
  status: string
  source: string
  operation_type: string | null
  description: string | null
  created_at: string
}

export interface WithdrawalRow {
  id: string
  rail: 'sepa' | 'crypto'
  amount: string
  currency: string
  status: string
  iban: string | null
  beneficiary: string | null
  address: string | null
  network: string | null
  reference: string
  /** 'polygon' + the tx hash once the payout job signed it (2026-10-11). */
  vendor?: string | null
  vendor_ref?: string | null
  /** 'auto' when approved within the caps, else the operator's id. */
  reviewed_by?: string | null
  failure_reason: string | null
  created_at: string
  settled_at: string | null
}

export interface KycDetail {
  id: string
  status: string
  provider: string | null
  level_name: string | null
  rejection_reason: string | null
  current_step: number
  total_steps: number
  created_at: string
}

/** Everything attached to one person, assembled server-side. */
export interface AdminCryptoDeposit {
  id: string
  tx_hash: string
  /** From the ERC-20 Transfer log. Never operator-editable. */
  amount: string
  currency: string
  network: string
  from_address: string | null
  status: 'PENDING' | 'CREDITED' | 'REJECTED'
  reject_reason: string | null
  created_at: string | null
  confirmed_at: string | null
  explorer: string
}

export interface CustomerProfile {
  customer: CustomerRow
  balances: Balance[]
  /** Savings positions — NOT part of `balances`. */
  savings: SavingsPosition[]
  cards: CardDetail[]
  transactions: TxnRow[]
  deposits: TxnRow[]
  /** USDT deposits. Since 2026-10-11 they credit themselves on arrival. */
  crypto_deposits: AdminCryptoDeposit[]
  /** Their own USDT deposit address — null until they open the deposit page. */
  deposit_address: {
    address: string
    assigned_at: string | null
    needs_forward: boolean
    last_forwarded_at: string | null
    explorer: string
  } | null
  withdrawals: WithdrawalRow[]
  kyc: KycDetail | null
  notes: { id: string; content: string; created_at: string }[]
  /** The app-lock PIN: whether one is set and whether it is locked out. Never the PIN. */
  pin: { enabled: boolean; locked: boolean }
  /** Whether this deployment may ask the issuer to freeze a card (`CARD_FREEZE_ENABLED`). */
  card_freeze: boolean
}

export interface QueueItem {
  key: string
  kind: QueueKind
  /** Sort key — oldest first, because waiting longest matters most. */
  createdAt: string
  payout?: Withdrawal
  identity?: KycRow
}

/* ── calls ─────────────────────────────────────────────────────────────── */

/* ── treasury ──────────────────────────────────────────────────────────── */

/**
 * Solvency compares what USERS ARE OWED against everywhere their money sits.
 *
 *     POOL = treasury + shared card pool + in-flight card holds
 *
 * 🔴 CORRECTED 2026-08-07. This said the card pool was "Tothor's working
 * capital, not backing" and deliberately excluded. There is no company money in
 * this product: the pool holds the CARDHOLDER's funds in transit, so excluding
 * it reported a shortfall the moment a card was funded — measured at −23.50 on
 * the first real application.
 *
 * §5b survives as a WARNING rather than an exclusion: the pool is meant to sit
 * at ~0 between card operations, so a resting balance is flagged even when the
 * verdict passes. See CUSTODY_AND_LICENSING.md §3.
 */
export interface Solvency {
  /**
   * `null` in a brand view (2026-10-14): the treasury is one pot for both
   * brands, so only Global can set claims against it. `scope` says which.
   */
  verdict: 'SOLVENT' | 'SHORTFALL' | 'UNKNOWN' | null
  scope: 'all' | 'tothor' | '1cards'
  buffer: string | null
  claims: {
    /**
     * 🔴 NULLABLE, and it must be rendered as such. `null` means a savings
     * position could not be priced, so what users are owed is UNKNOWN — not
     * zero. `money()` printed `0.00` for it until 2026-09-18 (`Number(null) === 0`);
     * it now prints a dash, but the views still say WHY it is unknown — keep the
     * guard, because a dash explains nothing to an operator.
     */
    claim: string | null
    pending_holds: string
    orphans: number
    /**
     * The part of `claim` that is price-tracked savings marked to market —
     * gold and TTR positions. Backed by nothing in the treasury by operator
     * decision (2026-08-31), so an operator reading a buffer needs to know how
     * much of the claim can move overnight on its own. `null` when unpriceable.
     */
    tracked_savings: string | null
    positions: Record<string, { quantity: string; rate: string | null; value: string | null }>
    /** Why the claim is unknown, when it is. */
    error: string | null
    /**
     * Tokens GIVEN rather than bought (`source='ttr_grant'`), by asset. NOT in
     * `claim`: nothing was paid in, so nothing is owed back from the treasury.
     * Shown on its own so a grant is never silent. Absent on an older backend.
     */
    granted?: Record<string, string>
  }
  treasury: {
    configured: boolean
    address: string | null
    usdc: string | null
    usdt: string | null
    /**
     * Credited customer USDT still in their own deposit addresses, read from the
     * chain, waiting for the root job to move it into the treasury. Part of
     * `total` — client money in addresses Tothor controls.
     */
    deposit_addresses?: string | null
    total: string | null
    explorer?: string
    error?: string
  }
}

export interface DepositAddressSummary {
  /** Derived by the root job, not yet shown to anyone. */
  stock: number
  assigned: number
  awaiting_forward: number
}

export interface CryptoDeposit {
  id: string
  tx_hash: string
  /** From the Transfer log. Never operator-editable. */
  amount: string
  currency: string
  network: string
  from_address: string | null
  /** The customer's deposit address — or the treasury, on pre-2026-10-11 claims. */
  to_address: string | null
  status: 'PENDING' | 'CREDITED' | 'REJECTED'
  created_at: string | null
  explorer: string
  email?: string
  name?: string
}

export interface MerchantFloat {
  currency: string
  balance: string | null
  floor: number
  note: string
  issuing_enabled: boolean
  error?: string
}

export interface SweepRow {
  id: string
  user_id: string | null
  email: string | null
  swipelux_customer_id: string
  status: string
  amount_requested: string
  amount_confirmed: string | null
  tx_hash: string | null
  transfer_id: string | null
  failure_reason: string | null
  explorer: string | null
  created_at: string
  settled_at: string | null
}

export const api = {
  // auth
  login: (email: string, password: string) =>
    post<{ access_token: string; admin: Admin }>('/auth/login', { email, password }),
  /**
   * ⚠️ The server wraps it — `{admin: {...}}`. This returned the WRAPPER typed
   * as `Admin` until 2026-09-25, so after any page reload `admin.role` and
   * `admin.email` were undefined (the sidebar showed no operator email); only a
   * fresh sign-in, which reads `login().admin`, filled them.
   */
  me: async () => (await get<{ admin: Admin }>('/auth/me')).admin,
  env: () => get<{ env: string }>('/auth/env'),

  // customers — the console is organised around people, not tables
  customers: (search?: string) =>
    get<{ customers: CustomerRow[] }>('/customers/', search ? { search } : undefined),
  customer: (id: string) => get<CustomerProfile>(`/customers/${id}`),

  // decisions — called from the customer's own page, in context
  approveWithdrawal: (id: string) => post<{ success: boolean }>(`/withdrawals/${id}/approve`),
  rejectWithdrawal: (id: string, reason: string) =>
    post<{ success: boolean }>(`/withdrawals/${id}/reject`, { reason }),
  /**
   * A PAYOUT_FAILED withdrawal back to the payout job. The SERVER reads the
   * chain and refuses unless nothing can still land — never a double payout.
   */
  retryWithdrawal: (id: string) => post<{ success: boolean }>(`/withdrawals/${id}/retry`),
  // 🗑 `markWithdrawalSent` / `markWithdrawalReturned` went with SEPA
  // (2026-10-14): the job records SENT itself, and nothing can be "returned".

  // identity
  approveKyc: (id: string) => post<unknown>(`/kyc/${id}/approve`),

  // people
  /** READ-ONLY vendor data — one call per vendor, see the types above. */
  vendorSumsub: (id: string) => get<VendorSumsub>(`/customers/${id}/vendors/sumsub`),
  vendorSwipelux: (id: string) => get<VendorSwipelux>(`/customers/${id}/vendors/swipelux`),
  vendorHypercard: (id: string) => get<VendorHypercard>(`/customers/${id}/vendors/hypercard`),
  /** Identity photographs. Audited server-side — fetched only when asked for. */
  vendorSumsubDocuments: (id: string) =>
    get<{ images: Record<string, string>; error: string | null }>(
      `/customers/${id}/vendors/sumsub/documents`),
  /**
   * Open a session in the customer's own app. Returns a single-use link that
   * dies in 60 seconds — see core/managers/impersonation.py.
   */
  impersonate: (id: string) =>
    post<{ url: string; email: string; brand: string; expires_in: number }>(`/users/${id}/impersonate`),
  /**
   * Set (or replace) the customer's 6-digit app PIN and clear any lockout.
   * ADMIN and above; audited; the customer is emailed that support did it.
   */
  setPin: (id: string, pin: string) =>
    post<{ ok: boolean; replaced: boolean }>(`/users/${id}/pin`, { pin }),
  /**
   * Reset the customer's Sumsub check so they can send new documents — any
   * state, including after a FINAL rejection. A verified customer keeps access
   * until the new verdict. `note` goes to the audit log only; the customer is
   * emailed a request to resubmit.
   */
  /**
   * Ask the issuer to freeze / unfreeze a card. REQUESTED, not done: the card's
   * status moves when the issuer's push confirms (`core/managers/card_ops.py`).
   * `cards:write`; audited with the optional internal note.
   */
  cardOp: (userId: string, cardId: string, op: 'freeze' | 'unfreeze', note?: string) =>
    post<{ requested: boolean; operation: string; request_number: string }>(
      `/users/${userId}/cards/${cardId}/${op}`, { note }),
  restartKyc: (id: string, note?: string) =>
    post<{ ok: boolean; reset: boolean; keep_access?: boolean }>(`/users/${id}/kyc/restart`, { note }),
  // ⚠️ Suspend/unsuspend, KYC reject and force-verify exist server-side with NO
  // button, by decision (2026-09-18): they were kept as curl-only powers when the
  // dead routes went. Their wrappers were removed, so an audit reading this file
  // sees no caller — which is true. (Operator accounts were in that list until
  // they got the Team screen, 2026-10-14 — below.)

  // my account — every operator, whatever the role
  updateProfile: async (body: { first_name?: string; last_name?: string }) =>
    (await patch<{ admin: Admin }>('/auth/profile', body)).admin,
  changePassword: (current_password: string, new_password: string) =>
    post<{ success: boolean }>('/auth/change-password', { current_password, new_password }),

  // team — operator accounts, SUPER_ADMIN only (`admins:write`). The server
  // keeps one active super admin at all times and refuses to delete anyone with
  // a history; see admin/backend/routers/users.py.
  operators: () => get<{ admin_users: Operator[] }>('/users/admins/list'),
  roleMatrix: () => get<RoleMatrix>('/users/admins/roles'),
  operatorActivity: (id: string, limit = 100) =>
    get<{ items: OperatorAction[] }>(`/users/admins/${id}/activity`, { limit }),
  createOperator: (body: {
    email: string; password: string; first_name: string; last_name: string; role: AdminRole
  }) => post<{ admin: Operator }>('/users/admins/create', body),
  updateOperator: (id: string, body: Partial<{
    email: string; first_name: string; last_name: string; role: AdminRole; is_active: boolean
  }>) => patch<{ admin: Operator }>(`/users/admins/${id}`, body),
  setOperatorPassword: (id: string, password: string) =>
    post<{ success: boolean }>(`/users/admins/${id}/password`, { password }),
  deleteOperator: (id: string) => del<{ success: boolean }>(`/users/admins/${id}`),

  // treasury — where the money physically is, vs what users are owed
  solvency: () => get<Solvency>('/treasury/solvency'),

  /**
   * Customer USDT deposits — READ-ONLY. Each customer pays into their own
   * address and a finalized arrival credits itself (2026-10-11); there is
   * nothing for an operator to confirm. `addresses.awaiting_forward` counts
   * addresses still holding credited USDT that has not reached the treasury.
   */
  cryptoDeposits: (limit = 50) =>
    get<{ items: CryptoDeposit[]; addresses: DepositAddressSummary }>(
      '/treasury/deposits', { limit }),
  merchantFloat: () => get<MerchantFloat>('/treasury/float'),
  sweeps: (status?: string) =>
    get<{ items: SweepRow[]; total: number }>('/treasury/sweeps', status ? { status } : undefined),
  /** Idempotent by construction — the same external_id-keyed path the cron uses. */
  retrySweep: (userId: string) => post<unknown>(`/treasury/customers/${userId}/sweep/retry`),

  // transfers between customers — read only; a transfer is final (2026-10-14)
  transfers: (limit = 500) =>
    get<{ items: TransferRow[]; limits: { min: string; per_transfer: string; daily: string } }>(
      '/transfers/', { limit }),

  // referrals — monitored here, paid by the invitee's KYC verdict (2026-09-25)
  referrals: () => get<ReferralsOverview>('/referrals/'),
  /**
   * SUPER_ADMIN only (`finance:write`). Writes the inverse ledger row of a paid
   * bonus, or stops a pending one from ever paying. `reason` is internal.
   */
  revokeReferral: (id: string, reason: string) =>
    post<{ ok: boolean; status: string; was: string }>(`/referrals/${id}/revoke`, { reason }),

  // prospects — Tothor only (2026-11-01)
  prospects: () => get<{ items: Prospect[] }>('/prospects/'),
  prospectStats: () => get<ProspectStats>('/prospects/stats'),
  prospect: (id: string) => get<ProspectDetail>(`/prospects/${id}`),
  createProspect: (body: { full_name: string; email?: string; phone?: string; birth_year?: number }) =>
    post<ProspectDetail>('/prospects/', body),
  updateProspect: (id: string, body: ProspectPatch) => patch<ProspectDetail>(`/prospects/${id}`, body),
  addProspectNote: (id: string, body: string) =>
    post<ProspectDetail>(`/prospects/${id}/notes`, { body }),
}
